<template>
  <div class="public-shop">
    <header class="shop-header" :style="{ background: themeGradientStyle }">
      <div class="container py-3">
        <router-link :to="shopPath('/kursus')" class="text-white" style="text-decoration:none;">
          <i class="fas fa-arrow-left"></i> Kembali ke Katalog
        </router-link>
      </div>
    </header>

    <div class="container mt-4" v-if="loading">
      <div class="text-center p-5"><div class="spinner-border"></div></div>
    </div>

    <div class="container mt-4" v-else-if="course">
      <div class="row">
        <div class="col-md-8">
          <div class="card mb-4 overflow-hidden">
            <img :src="courseImage(course.cover_image_url)" class="w-100 course-cover" :alt="course.title">
            <div class="card-body p-4">
              <h2>{{ course.title }}</h2>
              <p class="text-muted" v-if="course.description">{{ course.description }}</p>
              <h4 :class="course.is_free ? 'text-success' : 'text-primary'">
                {{ course.is_free ? 'GRATIS' : formatRupiah(course.price) }}
              </h4>
            </div>
          </div>

          <div class="card p-4 mb-4">
            <h5>Kurikulum</h5>
            <div v-for="(mod, mi) in course.modules" :key="mod.id" class="mb-3">
              <h6 class="text-muted">Modul {{ mi + 1 }}: {{ mod.title }}</h6>
              <ul class="list-unstyled ml-3">
                <li v-for="lesson in mod.lessons" :key="lesson.id" class="py-1">
                  <i class="fas" :class="lessonIcon(lesson.content_type)"></i>
                  {{ lesson.title }}
                  <span v-if="lesson.is_preview" class="badge badge-info ml-1">Preview</span>
                  <small v-if="lesson.duration_minutes" class="text-muted ml-2">{{ lesson.duration_minutes }} menit</small>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div class="col-md-4">
          <div class="card p-4 sticky-top" style="top:20px;">
            <h5>{{ course.is_free ? 'Daftar Gratis' : 'Beli Kursus' }}</h5>
            <div class="mb-2">
              <label>Nama</label>
              <input v-model="form.student_name" class="form-control" placeholder="Nama Anda">
            </div>
            <div class="mb-2">
              <label>WhatsApp *</label>
              <input v-model="form.student_phone" class="form-control" required>
            </div>
            <div class="mb-2">
              <label>Email</label>
              <input v-model="form.student_email" type="email" class="form-control">
            </div>

            <div v-if="!course.is_free" class="mb-3">
              <label>Metode Pembayaran</label>
              <PaymentChannelPicker
                :channels="paymentChannels"
                :loading="loadingChannels"
                v-model="form.payment_method"
              />

              <div v-if="isTripayMethod && selectedChannel" class="mt-2 small">
                <div class="d-flex justify-content-between">
                  <span>Harga kursus:</span>
                  <span>{{ formatRupiah(course.price) }}</span>
                </div>
                <div class="d-flex justify-content-between text-muted">
                  <span>Fee gateway:</span>
                  <span>{{ formatRupiah(gatewayFee) }}</span>
                </div>
                <div class="d-flex justify-content-between mt-1">
                  <b>Total bayar:</b>
                  <b class="text-primary">{{ formatRupiah(totalPay) }}</b>
                </div>
              </div>
            </div>

            <button class="btn btn-primary btn-block btn-lg" @click="enroll" :disabled="submitting || !canEnroll">
              <span v-if="submitting" class="spinner-border spinner-border-sm mr-1"></span>
              {{ course.is_free ? 'Daftar Sekarang' : 'Lanjut Bayar' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import publicShopPaths from './public_shop_paths';
import PaymentChannelPicker from '@/components/shop_public/PaymentChannelPicker.vue';
import { applyShopTheme, shopHeaderBackground, pickShopTheme, courseImage, shopPublicMixin } from '@/components/shop_public/shop_public_utils';
import { trackViewContent, trackInitiateCheckout } from '@/components/shop_public/shop_tracking';
import '@/components/shop_public/shop_public.css';

export default {
  mixins: [publicShopPaths, shopPublicMixin],
  components: { PaymentChannelPicker },
  data() {
    return {
      loading: true, submitting: false, loadingChannels: false,
      course: null, themeColor: '#1565c0',
      form: { student_name: '', student_phone: '', student_email: '', payment_method: '' },
      paymentChannels: [],
    };
  },
  computed: {
    selectedChannel() {
      return this.paymentChannels.find(ch => ch.code === this.form.payment_method) || null;
    },
    themeGradientStyle() {
      return shopHeaderBackground(pickShopTheme(this.course, this.$store.state.public_shop.shop));
    },
    isTripayMethod() {
      const m = this.form.payment_method;
      return m && m !== 'transfer_manual' && m !== 'cod';
    },
    gatewayFee() {
      if (!this.isTripayMethod || !this.selectedChannel || !this.course) return 0;
      return this.calcGatewayFee(this.selectedChannel, this.course.price);
    },
    totalPay() {
      if (!this.course) return 0;
      return this.course.price + (this.isTripayMethod ? this.gatewayFee : 0);
    },
    canEnroll() {
      if (!this.form.student_phone) return false;
      if (this.course?.is_free) return true;
      return !!this.form.payment_method;
    },
  },
  async mounted() {
    if (!this.effectiveSlug && this.onCustomDomain) {
      await this.$store.dispatch('public_shop/public_get_shop_by_domain');
    }
    await this.load();
  },
  methods: {
    courseImage,
    lessonIcon(t) {
      return { video: 'fa-play-circle', text: 'fa-file-alt', pdf: 'fa-file-pdf', quiz: 'fa-question-circle' }[t] || 'fa-book';
    },
    calcGatewayFee(ch, amount) {
      let fee = ch.fee_flat || 0;
      if (ch.fee_percent) fee += Math.round(amount * ch.fee_percent / 100);
      return fee;
    },
    async load() {
      try {
        const slug = this.$route.params.courseSlug;
        const r = await this.$store.dispatch('public_shop/public_get_lms_course', { slug: this.effectiveSlug, courseSlug: slug });
        this.course = r.data.data.course;
        applyShopTheme(pickShopTheme(this.course, this.$store.state.public_shop.shop));
        trackViewContent({
          course_id: this.course.id,
          product_id: this.course.id,
          product_name: this.course.title,
          name: this.course.title,
          price: this.course.price || 0,
          content_type: 'product',
          type: 'course',
        });
        if (!this.course.is_free) await this.loadPaymentChannels();
      } catch (e) {
        this.$toast.error('Kursus tidak ditemukan');
      } finally { this.loading = false; }
    },
    async loadPaymentChannels() {
      this.loadingChannels = true;
      try {
        const base = (axios.defaults.baseURL || '').replace(/\/api\/v1\/?$/, '');
        const r = await axios.get(base + '/api/public/shop/' + this.effectiveSlug + '/payment-channels', { headers: { Authorization: '' } });
        this.paymentChannels = r.data.data?.channels || [];
      } catch (e) { /* noop */ }
      finally { this.loadingChannels = false; }
    },
    async enroll() {
      if (!this.form.student_phone) {
        this.$toast.error('Nomor WhatsApp wajib diisi');
        return;
      }
      if (!this.course.is_free && !this.form.payment_method) {
        this.$toast.error('Pilih metode pembayaran');
        return;
      }
      trackInitiateCheckout([{
        product_id: this.course.id,
        product_name: this.course.title,
        price: this.course.price,
        quantity: 1,
        type: 'course',
      }], this.totalPay);
      this.submitting = true;
      try {
        const slug = this.$route.params.courseSlug;
        const payload = { ...this.form };
        if (this.course.is_free) delete payload.payment_method;
        const r = await this.$store.dispatch('public_shop/public_create_enrollment', {
          slug: this.effectiveSlug, courseSlug: slug, payload,
        });
        const data = r.data.data;
        if (data.is_free) {
          this.$router.push(this.shopPath('/kursus/' + slug + '/belajar?token=' + data.enrollment.access_token));
          return;
        }
        const en = data.enrollment;
        if (this.form.payment_method !== 'transfer_manual') {
          const pr = await this.$store.dispatch('public_shop/public_get_enrollment_payment', {
            enrollmentNumber: en.enrollment_number, method: this.form.payment_method,
          });
          const url = pr.data.data.payment_url;
          if (url) { window.location.href = url; return; }
        }
        this.$router.push(this.shopPath('/kursus/' + slug + '/enroll/' + en.enrollment_number));
      } catch (e) {
        this.$toast.error(e.response?.data?.message || 'Gagal mendaftar');
      } finally { this.submitting = false; }
    },
  },
};
</script>

<style scoped>
.shop-header { color: white; }
.course-cover {
  max-height: 280px;
  object-fit: cover;
}
.course-cover-fallback {
  height: 200px;
}
</style>
